import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { User, getCurrentUser, updateUserProfile } from '@/services/authService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { 
  User as UserIcon, 
  Settings, 
  BookOpen, 
  Award, 
  CreditCard, 
  Clock, 
  Save, 
  Upload, 
  AlertCircle 
} from 'lucide-react';

export default function ProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  
  // Form state
  const [formData, setFormData] = useState({
    full_name: '',
    bio: '',
    preferred_language: 'vi',
    learning_path: 'beginner',
    areas_of_interest: [] as string[],
    meditation_experience: 'none',
    buddhist_school: 'none',
  });
  
  // Tải thông tin user
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const userData = await getCurrentUser();
        
        if (!userData) {
          navigate('/login');
          return;
        }
        
        setUser(userData);
        
        // Cập nhật form data từ user data
        setFormData({
          full_name: userData.full_name || '',
          bio: userData.bio || '',
          preferred_language: userData.preferred_language || 'vi',
          learning_path: userData.learning_path || 'beginner',
          areas_of_interest: userData.areas_of_interest || [],
          meditation_experience: userData.meditation_experience || 'none',
          buddhist_school: userData.buddhist_school || 'none',
        });
      } catch (error) {
        console.error('Lỗi khi lấy thông tin user:', error);
        setError(t('errors.loadFailed', { ns: 'profile' }));
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [navigate, t]);
  
  // Xử lý thay đổi input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Xử lý thay đổi select
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Xử lý lưu thông tin
  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      if (!user) return;
      
      await updateUserProfile(user.id, {
        full_name: formData.full_name,
        bio: formData.bio,
        preferred_language: formData.preferred_language,
        learning_path: formData.learning_path,
        areas_of_interest: formData.areas_of_interest,
        meditation_experience: formData.meditation_experience,
        buddhist_school: formData.buddhist_school,
      });
      
      setSuccess(t('success.profileUpdated', { ns: 'profile' }));
      
      // Cập nhật lại thông tin user
      const updatedUser = await getCurrentUser();
      setUser(updatedUser);
    } catch (error) {
      console.error('Lỗi khi cập nhật thông tin:', error);
      setError(t('errors.updateFailed', { ns: 'profile' }));
    } finally {
      setSaving(false);
    }
  };
  
  // Lấy chữ cái đầu của tên user để hiển thị trong avatar
  const getUserInitials = () => {
    if (!user || !user.full_name) return '?';
    
    const nameParts = user.full_name.split(' ');
    if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();
    
    // Lấy chữ cái đầu của tên và họ
    const firstName = nameParts[0];
    const lastName = nameParts[nameParts.length - 1];
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
  };
  
  // Tính toán ngày hết hạn subscription
  const subscriptionEndDate = user?.subscription_end_date 
    ? new Date(user.subscription_end_date) 
    : null;
  
  // Tính số ngày còn lại của subscription
  const daysRemaining = subscriptionEndDate 
    ? Math.max(0, Math.ceil((subscriptionEndDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))) 
    : 0;
  
  // Tính phần trăm AI credits đã sử dụng
  const aiCreditsPercentage = user?.ai_credits 
    ? Math.min(100, Math.round((user.ai_credits_used || 0) / user.ai_credits * 100)) 
    : 0;
  
  if (loading) {
    return (
      <div className="container py-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-1/3 bg-muted rounded"></div>
            <div className="h-64 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container py-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6 flex items-center gap-2">
          <UserIcon className="h-8 w-8" />
          {t('title', { ns: 'profile' })}
        </h1>
        
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {success && (
          <Alert className="mb-4 bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400">
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Sidebar */}
          <div className="md:col-span-1 space-y-4">
            <Card>
              <CardContent className="pt-6 flex flex-col items-center">
                <Avatar className="h-24 w-24 mb-4">
                  <AvatarImage src={user?.avatar} alt={user?.full_name} />
                  <AvatarFallback className="text-2xl">{getUserInitials()}</AvatarFallback>
                </Avatar>
                <h2 className="text-xl font-semibold">{user?.full_name}</h2>
                <p className="text-muted-foreground">{user?.email}</p>
                <div className="mt-2 text-sm bg-primary/10 text-primary px-3 py-1 rounded-full">
                  {user?.is_premium ? t('premiumMember', { ns: 'user' }) : t('freeMember', { ns: 'user' })}
                </div>
                
                <Separator className="my-4" />
                
                <div className="w-full space-y-3">
                  {/* Subscription info */}
                  {user?.is_premium && (
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{t('subscription', { ns: 'user' })}:</span>
                        <span className="font-medium">{user.subscription_plan}</span>
                      </div>
                      {subscriptionEndDate && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{t('expiresIn', { ns: 'user' })}:</span>
                          <span className="font-medium">
                            {daysRemaining} {t('days', { ns: 'user' })}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* AI Credits */}
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('aiCredits', { ns: 'user' })}:</span>
                      <span className="font-medium">
                        {user?.ai_credits || 0}
                      </span>
                    </div>
                    <Progress value={100} className="h-2" />
                  </div>
                  
                  {/* Learning time */}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('learningTime', { ns: 'user' })}:</span>
                    <span className="font-medium">
                      {Math.floor((user?.total_learning_time || 0) / 60)} {t('hours', { ns: 'user' })} {(user?.total_learning_time || 0) % 60} {t('minutes', { ns: 'user' })}
                    </span>
                  </div>
                </div>
                
                <Separator className="my-4" />
                
                {!user?.is_premium && (
                  <Button 
                    className="w-full" 
                    onClick={() => navigate('/subscription')}
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    {t('upgradeToPremium', { ns: 'user' })}
                  </Button>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>{t('quickLinks', { ns: 'profile' })}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="ghost" 
                  className="w-full justify-start" 
                  onClick={() => navigate('/learning-dashboard')}
                >
                  <BookOpen className="mr-2 h-4 w-4" />
                  {t('learningDashboard', { ns: 'user' })}
                </Button>
                <Button 
                  variant="ghost" 
                  className="w-full justify-start" 
                  onClick={() => navigate('/achievements')}
                >
                  <Award className="mr-2 h-4 w-4" />
                  {t('achievements', { ns: 'user' })}
                </Button>
                <Button 
                  variant="ghost" 
                  className="w-full justify-start" 
                  onClick={() => navigate('/ai-chat')}
                >
                  <Clock className="mr-2 h-4 w-4" />
                  {t('aiChat', { ns: 'user' })}
                </Button>
                <Button 
                  variant="ghost" 
                  className="w-full justify-start" 
                  onClick={() => setActiveTab('settings')}
                >
                  <Settings className="mr-2 h-4 w-4" />
                  {t('settings', { ns: 'user' })}
                </Button>
              </CardContent>
            </Card>
          </div>
          
          {/* Main content */}
          <div className="md:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="profile">
                  <UserIcon className="mr-2 h-4 w-4" />
                  {t('tabs.profile', { ns: 'profile' })}
                </TabsTrigger>
                <TabsTrigger value="learning">
                  <BookOpen className="mr-2 h-4 w-4" />
                  {t('tabs.learning', { ns: 'profile' })}
                </TabsTrigger>
                <TabsTrigger value="settings">
                  <Settings className="mr-2 h-4 w-4" />
                  {t('tabs.settings', { ns: 'profile' })}
                </TabsTrigger>
              </TabsList>
              
              {/* Profile Tab */}
              <TabsContent value="profile">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('personalInfo', { ns: 'profile' })}</CardTitle>
                    <CardDescription>
                      {t('personalInfoDesc', { ns: 'profile' })}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="full_name">{t('fullName', { ns: 'profile' })}</Label>
                      <Input
                        id="full_name"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleInputChange}
                        placeholder={t('fullNamePlaceholder', { ns: 'profile' })}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">{t('email', { ns: 'profile' })}</Label>
                      <Input
                        id="email"
                        value={user?.email || ''}
                        disabled
                        className="bg-muted"
                      />
                      <p className="text-sm text-muted-foreground">
                        {t('emailChangeNote', { ns: 'profile' })}
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="bio">{t('bio', { ns: 'profile' })}</Label>
                      <Textarea
                        id="bio"
                        name="bio"
                        value={formData.bio}
                        onChange={handleInputChange}
                        placeholder={t('bioPlaceholder', { ns: 'profile' })}
                        rows={4}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="preferred_language">{t('preferredLanguage', { ns: 'profile' })}</Label>
                      <Select
                        value={formData.preferred_language}
                        onValueChange={(value) => handleSelectChange('preferred_language', value)}
                      >
                        <SelectTrigger id="preferred_language">
                          <SelectValue placeholder={t('selectLanguage', { ns: 'profile' })} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vi">Tiếng Việt</SelectItem>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="pi">Pāli</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>{t('avatar', { ns: 'profile' })}</Label>
                      <div className="flex items-center gap-4">
                        <Avatar className="h-16 w-16">
                          <AvatarImage src={user?.avatar} alt={user?.full_name} />
                          <AvatarFallback>{getUserInitials()}</AvatarFallback>
                        </Avatar>
                        <Button variant="outline" disabled>
                          <Upload className="mr-2 h-4 w-4" />
                          {t('uploadAvatar', { ns: 'profile' })}
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t('avatarNote', { ns: 'profile' })}
                      </p>
                    </div>
                  </CardContent>
                  <CardFooter className="flex justify-end">
                    <Button onClick={handleSaveProfile} disabled={saving}>
                      {saving ? (
                        <span className="flex items-center gap-2">
                          <span className="animate-spin">⏳</span>
                          {t('common.saving')}
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Save className="h-4 w-4" />
                          {t('common.save')}
                        </span>
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              </TabsContent>
              
              {/* Learning Tab */}
              <TabsContent value="learning">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('learningPreferences', { ns: 'profile' })}</CardTitle>
                    <CardDescription>
                      {t('learningPreferencesDesc', { ns: 'profile' })}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="learning_path">{t('learningPath', { ns: 'profile' })}</Label>
                      <Select
                        value={formData.learning_path}
                        onValueChange={(value) => handleSelectChange('learning_path', value)}
                      >
                        <SelectTrigger id="learning_path">
                          <SelectValue placeholder={t('selectLearningPath', { ns: 'profile' })} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">{t('learningPaths.beginner', { ns: 'profile' })}</SelectItem>
                          <SelectItem value="intermediate">{t('learningPaths.intermediate', { ns: 'profile' })}</SelectItem>
                          <SelectItem value="advanced">{t('learningPaths.advanced', { ns: 'profile' })}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="meditation_experience">{t('meditationExperience', { ns: 'profile' })}</Label>
                      <Select
                        value={formData.meditation_experience}
                        onValueChange={(value) => handleSelectChange('meditation_experience', value)}
                      >
                        <SelectTrigger id="meditation_experience">
                          <SelectValue placeholder={t('selectMeditationExperience', { ns: 'profile' })} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t('meditationLevels.none', { ns: 'profile' })}</SelectItem>
                          <SelectItem value="beginner">{t('meditationLevels.beginner', { ns: 'profile' })}</SelectItem>
                          <SelectItem value="intermediate">{t('meditationLevels.intermediate', { ns: 'profile' })}</SelectItem>
                          <SelectItem value="advanced">{t('meditationLevels.advanced', { ns: 'profile' })}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="buddhist_school">{t('buddhistSchool', { ns: 'profile' })}</Label>
                      <Select
                        value={formData.buddhist_school}
                        onValueChange={(value) => handleSelectChange('buddhist_school', value)}
                      >
                        <SelectTrigger id="buddhist_school">
                          <SelectValue placeholder={t('selectBuddhistSchool', { ns: 'profile' })} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t('buddhistSchools.none', { ns: 'profile' })}</SelectItem>
                          <SelectItem value="theravada">{t('buddhistSchools.theravada', { ns: 'profile' })}</SelectItem>
                          <SelectItem value="mahayana">{t('buddhistSchools.mahayana', { ns: 'profile' })}</SelectItem>
                          <SelectItem value="vajrayana">{t('buddhistSchools.vajrayana', { ns: 'profile' })}</SelectItem>
                          <SelectItem value="zen">{t('buddhistSchools.zen', { ns: 'profile' })}</SelectItem>
                          <SelectItem value="pure-land">{t('buddhistSchools.pureLand', { ns: 'profile' })}</SelectItem>
                          <SelectItem value="vietnamese-buddhism">{t('buddhistSchools.vietnameseBuddhism', { ns: 'profile' })}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                  <CardFooter className="flex justify-end">
                    <Button onClick={handleSaveProfile} disabled={saving}>
                      {saving ? (
                        <span className="flex items-center gap-2">
                          <span className="animate-spin">⏳</span>
                          {t('common.saving')}
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Save className="h-4 w-4" />
                          {t('common.save')}
                        </span>
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              </TabsContent>
              
              {/* Settings Tab */}
              <TabsContent value="settings">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('accountSettings', { ns: 'profile' })}</CardTitle>
                    <CardDescription>
                      {t('accountSettingsDesc', { ns: 'profile' })}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>{t('changePassword', { ns: 'profile' })}</Label>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start"
                        onClick={() => navigate('/change-password')}
                      >
                        {t('changePasswordButton', { ns: 'profile' })}
                      </Button>
                    </div>
                    
                    <Separator />
                    
                    <div className="space-y-2">
                      <Label>{t('deleteAccount', { ns: 'profile' })}</Label>
                      <p className="text-sm text-muted-foreground">
                        {t('deleteAccountWarning', { ns: 'profile' })}
                      </p>
                      <Button 
                        variant="destructive" 
                        className="w-full justify-start"
                        onClick={() => navigate('/delete-account')}
                      >
                        {t('deleteAccountButton', { ns: 'profile' })}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
} 